import { forwardRef, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { color } from '../../app/theme';

const getBorder = () => `1px solid ${color.$9}`;

const Table = styled('div').attrs(() => ({
  className: 'sticky-table',
}))`
  white-space: nowrap;
  display: table;
  box-sizing: border-box;
  width: 100%;
`;

Table.displayName = 'Table';

const Cell = styled('div').attrs(() => ({
  className: 'sticky-table-cell',
}))`
  display: table-cell;
  box-sizing: border-box;
`;

Cell.displayName = 'Cell';

const Row = styled('div').attrs(() => ({
  className: 'sticky-table-row',
}))`
  display: table-row;
`;

Row.displayName = 'Row';

const Wrapper = styled('div').attrs(() => ({
  className: 'sticky-table-wrapper',
}))`
  position: relative;
  overflow: auto;
  box-sizing: border-box;
  & ${Row}:nth-child(${props => `-n+${props.stickyHeaderCount}`}) ${Cell} {
    position: -webkit-sticky;
    position: sticky;
    top: 0px;
    z-index: ${props => props.headerZ || 2};
  }
  & ${Row}:nth-last-child(-n+${props => props.stickyFooterCount}) ${Cell} {
    position: -webkit-sticky;
    position: sticky;
    bottom: 0px;
    z-index: ${props => props.footerZ || 2};
  }
  & ${Row} ${Cell}:nth-child(-n+${props => props.leftStickyColumnCount}) {
    position: -webkit-sticky;
    position: sticky;
    left: 0px;
    z-index: ${props => props.leftColumnZ || 2};
  }
  & ${Row} ${Cell}:nth-last-child(-n+${props => props.rightStickyColumnCount}) {
    position: -webkit-sticky;
    position: sticky;
    right: 0px;
    z-index: ${props => props.rightColumnZ || 2};
  }
  & ${Row}:nth-child(${props => `${props.stickyHeaderCount}`}) ${Cell} {
    border-bottom: ${getBorder};
  }
  & ${Row}:nth-last-child(${props => props.stickyFooterCount}) ${Cell} {
    border-top: ${getBorder};
  }
  & ${Row} ${Cell}:nth-child(${props => props.leftStickyColumnCount}) {
    border-right: ${getBorder};
  }
  & ${Row} ${Cell}:nth-last-child(${props => props.rightStickyColumnCount}) {
    border-left: ${getBorder};
  }
  ${props => {
    const insets = props.stickyInsets;
    let styles = '';
    let i;
    for (i = 0; i < insets.header.length; i += 1) {
      styles += `& ${Row}:nth-child(${i + 1}) ${Cell} { top: ${
        insets.header[i]
      }px; }`;
    }
    for (i = 0; i < insets.footer.length; i += 1) {
      styles += `& ${Row}:nth-last-child(${i + 1}) ${Cell} { bottom: ${
        insets.footer[i]
      }px; }`;
    }
    for (i = 0; i < insets.leftColumn.length; i += 1) {
      styles += `& ${Row} ${Cell}:nth-child(${i + 1}) { left: ${
        insets.leftColumn[i]
      }px; }`;
    }
    for (i = 0; i < insets.rightColumn.length; i += 1) {
      styles += `& ${Row} ${Cell}:nth-last-child(${i + 1}) { right: ${
        insets.rightColumn[i]
      }px; }`;
    }
    return css`
      ${styles}
    `;
  }}
  & ${Row}:nth-child(-n+${props =>
    props.stickyHeaderCount}) ${Cell}:nth-child(-n+${props =>
    props.leftStickyColumnCount}) {
    z-index: ${props =>
      Math.max(props.headerZ || 2, props.leftColumnZ || 2) + 1};
  }
  &
    ${Row}:nth-child(-n+${props => props.stickyHeaderCount})
    ${Cell}:nth-last-child(-n+${props => props.rightStickyColumnCount}) {
    z-index: ${props =>
      Math.max(props.headerZ || 2, props.rightColumnZ || 2) + 1};
  }
  &
    ${Row}:nth-last-child(-n+${props => props.stickyFooterCount})
    ${Cell}:nth-child(-n+${props => props.leftStickyColumnCount}) {
    z-index: ${props =>
      Math.max(props.footerZ || 2, props.leftColumnZ || 2) + 1};
  }
  &
    ${Row}:nth-last-child(-n+${props => props.stickyFooterCount})
    ${Cell}:nth-last-child(-n+${props => props.rightStickyColumnCount}) {
    z-index: ${props =>
      Math.max(props.footerZ || 2, props.rightColumnZ || 2) + 1};
  }
`;

Wrapper.displayName = 'Wrapper';

/**
 * 被鎖定的目標寬度高度必須定死寬高不能變動，否則會造成計算錯誤。
 * 若被鎖定的目標寬度高度會變動，則須每 1000/60 毫秒計算一次寬度，將會影響效能。
 */

let multipleStickiesInterval = null;

const StickyTable = forwardRef((props, ref) => {
  const {
    leftStickyColumnCount = 0,
    stickyHeaderCount = 0,
    stickyFooterCount = 0,
    rightStickyColumnCount = 0,
    children,
    isColumnsAutoSize = false,
    classNames,
  } = props;

  const tableNode = useRef();
  const [stickyInsets, setStickyInsets] = useState({
    header: [],
    footer: [],
    leftColumn: [],
    rightColumn: [],
  });

  const clearMultipleStickiesInterval = () => {
    if (isColumnsAutoSize && multipleStickiesInterval) {
      clearInterval(multipleStickiesInterval);
      multipleStickiesInterval = null;
    }
  };

  const checkForStickySizeChanges = () => {
    if (!isColumnsAutoSize || multipleStickiesInterval) {
      let s;
      const tempStickyInsets = {};
      const rowNodes =
        tableNode?.current?.querySelectorAll('.sticky-table-row');
      const cellNodes =
        tableNode?.current?.querySelectorAll('.sticky-table-cell');

      [
        ['header', 'height', 'stickyHeaderCount'],
        ['footer', 'height', 'stickyFooterCount'],
        ['leftColumn', 'width', 'leftStickyColumnCount'],
        ['rightColumn', 'width', 'rightStickyColumnCount'],
      ].forEach(([stickyKey, sizeKey, countPropKey]) => {
        let insets = [];

        if (props[countPropKey] > 1) {
          insets = [0];
          const count = props[countPropKey];
          let netInset = 0;

          // HINT we only want this loop for the second sticky and up
          for (s = 1; s < count; s += 1) {
            let node;

            switch (stickyKey) {
              case 'header':
                node = rowNodes[s - 1].childNodes[0];
                break;
              case 'footer':
                node = rowNodes[rowNodes.length - s].childNodes[0];
                break;
              case 'leftColumn':
                node = cellNodes[s - 1];
                break;
              case 'rightColumn':
                node = cellNodes[cellNodes.length - s];
                break;
              default:
                break;
            }

            if (node) {
              const boundingRect = node.getBoundingClientRect();

              netInset += boundingRect[sizeKey];
            }

            insets.push(netInset);
          }
        }

        tempStickyInsets[stickyKey] = insets;
      });

      // HINT avoid a render unless there's actually a change
      if (JSON.stringify(tempStickyInsets) !== JSON.stringify(stickyInsets)) {
        setStickyInsets(tempStickyInsets);
      }
    }
  };

  const considerSchedulingMultipleStickiesInterval = () => {
    const shouldSchedule = [
      stickyHeaderCount,
      stickyFooterCount,
      leftStickyColumnCount,
      rightStickyColumnCount,
    ].some(count => count > 1);

    if (!shouldSchedule && multipleStickiesInterval) {
      setStickyInsets({
        header: [],
        footer: [],
        leftColumn: [],
        rightColumn: [],
      });
    }

    clearMultipleStickiesInterval();

    if (shouldSchedule) {
      if (isColumnsAutoSize) {
        multipleStickiesInterval = setInterval(
          checkForStickySizeChanges,
          1000 / 60,
        );
      } else {
        checkForStickySizeChanges();
      }
    }
  };

  useEffect(() => {
    considerSchedulingMultipleStickiesInterval();
  }, [
    leftStickyColumnCount,
    stickyHeaderCount,
    stickyFooterCount,
    rightStickyColumnCount,
  ]);

  useEffect(() => {
    return () => {
      clearMultipleStickiesInterval();
    };
  }, []);

  return (
    <Wrapper ref={ref} stickyInsets={stickyInsets} {...props}>
      <Table className={classNames.table} ref={tableNode}>
        {children}
      </Table>
    </Wrapper>
  );
});

export { Cell, Row, StickyTable, Table };
